import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { getStorage, prefersDark } from "../platform";

type ThemeMode = "light" | "dark" | "system";

interface ThemeStore {
  mode: ThemeMode;
  resolved: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
}

function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") {
    return prefersDark() ? "dark" : "light";
  }
  return mode;
}

// In-memory fallback so `persist` never throws when the platform adapter
// isn't configured (e.g. unit tests). Real apps wire localStorage / MMKV.
const memoryStorage = (() => {
  const map = new Map<string, string>();
  return {
    getItem: (k: string) => map.get(k) ?? null,
    setItem: (k: string, v: string) => {
      map.set(k, v);
    },
    removeItem: (k: string) => {
      map.delete(k);
    },
  };
})();

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      mode: "system",
      resolved: resolveTheme("system"),
      setMode: (mode) => set({ mode, resolved: resolveTheme(mode) }),
    }),
    {
      name: "slumbr-theme",
      storage: createJSONStorage(() => {
        try {
          return getStorage();
        } catch {
          return memoryStorage;
        }
      }),
      // Defer hydration until the host app wires its platform adapter.
      // Without this, ESM hoisting would call `getStorage()` during
      // module load — before configurePlatform() runs — and persist
      // would silently latch onto the memoryStorage fallback, so the
      // theme would never survive a reload. Apps must call
      // `useThemeStore.persist.rehydrate()` after wiring the adapter.
      skipHydration: true,
    }
  )
);
